import { useEffect, useState, type FormEvent } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'

import { REPOSITORY_COPY } from '#app/routes/mocra+/_form+/components/repositories/constants.ts'
import { RepositoryForm } from '#app/routes/mocra+/_form+/components/repositories/form.tsx'
import {
	collectRepositoryErrors,
	createRepositoryDraft,
	sanitizeRepositoryDraft,
	type RepositoryDraft,
} from '#app/routes/mocra+/_form+/components/repositories/types.ts'

export type RepositoryOverlayMode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof RepositoryDraft, string>>

type Props = {
	readonly mode: RepositoryOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: RepositoryDraft) => void
	readonly initialValue?: RepositoryDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function RepositoryOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<RepositoryDraft>(
		initialValue ? { ...initialValue } : createRepositoryDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createRepositoryDraft())
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open) {
			onClosed?.()
		}
	}, [onClosed, open])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeRepositoryDraft(draft)
		const validationErrors = collectRepositoryErrors(sanitized)
		if (validationErrors.length > 0) {
			const nextErrors: DraftErrors = {}
			nextErrors.location = validationErrors[0]
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy =
		mode === 'add' ? REPOSITORY_COPY.title.add : REPOSITORY_COPY.title.edit
	const submitCopy =
		mode === 'add' ? REPOSITORY_COPY.submit.add : REPOSITORY_COPY.submit.edit

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={REPOSITORY_COPY.description}
			className="sm:max-w-xl"
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="repository-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<RepositoryForm
				formId="repository-overlay-form"
				draft={draft}
				errors={errors}
				onDraftFieldChange={(field, value) =>
					setDraft((prev) => ({ ...prev, [field]: value }))
				}
				onSubmit={handleSubmit}
			/>
		</ResponsiveOverlay>
	)
}
